import { DateTermV2 } from '../../../../domain/value-object/DateTermV2'
import { DateTimeRangeV2 } from '../../../../domain/value-object/DateTimeRangeV2'
import {
  DateTimeV2,
  dateTimeV2Service,
} from '../../../../domain/value-object/DateTimeV2'
import { DateV2, dateV2Service } from '../../../../domain/value-object/DateV2'
import {
  createDelta,
  IItemDelta,
} from '../../../../domain/value-object/ItemDeltaInputVO'
import { ReferencedEntity } from '../../../../domain/value-object/ReferencedEntity'
import { SlackUser } from '../../../../domain/value-object/Slack/UserVO'
import {
  MeUpdateDeltaInput,
  UserDetailDto,
} from '../../../../infrastructure/api/users'

// 項目と並びは uiMeta の property_layout に合わせている（1-19 の lastLoginAt は
// hidden_if_u が TRUE で表示しない項目のため持たない）。
export type MeFormModel = {
  code: string
  iconUrl: string
  name: string
  nameKana: string
  nameAlphabet: string
  email: string
  phoneNumber: string
  birthday: DateV2 | undefined
  timezoneId: string | undefined
  division: ReferencedEntity | undefined
  role: ReferencedEntity | undefined
  // 権限ポリシーは複数持てるが読み取り専用のため、名称を連結したテキストとして持つ。
  permissionPolicies: string
  // 画面では期間1項目だが、APIでは hireDate / leaveDate、validFrom / validTo に
  // 分かれている。
  hireLeaveDate: DateTermV2
  expirationDate: DateTimeRangeV2
  emailNotification: boolean
  partnerMember: boolean
  registrationStatus: string | undefined
  oneTimeTokenExpiredAt: DateTimeV2 | undefined
  // Slack連携はワークスペースごとに持てるが、セルが扱えるのは1件のみ。
  slackUser: SlackUser | undefined
  githubUserName: string
}

export const fromDetail = (detail: UserDetailDto): MeFormModel => ({
  code: detail.code || '',
  iconUrl: detail.iconUrl || '',
  name: detail.name,
  nameKana: detail.nameKana || '',
  nameAlphabet: detail.nameAlphabet || '',
  email: detail.email,
  phoneNumber: detail.phoneNumber || '',
  birthday: dateV2Service.fromApiResponseToVo(detail.birthday),
  timezoneId: detail.timezoneId,
  division: detail.division
    ? { uuid: detail.division.uuid, name: detail.division.displayName }
    : undefined,
  role: detail.role
    ? { uuid: detail.role.uuid, name: detail.role.name }
    : undefined,
  permissionPolicies: (detail.permissionPolicies ?? [])
    .map(v => v.name)
    .join(','),
  hireLeaveDate: {
    startDate: dateV2Service.fromApiResponseToVo(detail.hireDate),
    endDate: dateV2Service.fromApiResponseToVo(detail.leaveDate),
  },
  expirationDate: {
    startDateTime: dateTimeV2Service.fromApiResponseToVo(detail.validFrom),
    endDateTime: dateTimeV2Service.fromApiResponseToVo(detail.validTo),
  },
  emailNotification: detail.emailNotification,
  partnerMember: detail.partnerMember,
  registrationStatus: detail.registrationStatus,
  oneTimeTokenExpiredAt: dateTimeV2Service.fromApiResponseToVo(
    detail.oneTimeTokenExpiredAt
  ),
  slackUser: pickDisplayedSlackUser(detail.slackUser),
  githubUserName: detail.githubUserName || '',
})

// 実際に変更された項目だけを含む差分更新ペイロードを組み立てる。
// uuid は /me/delta では認証情報から上書きされるため送らない。
// code / email / division / role / permissionPolicies / hireLeaveDate /
// expirationDate / partnerMember / registrationStatus / oneTimeTokenExpiredAt は
// 表示専用のため含めない。本人による変更は UpdateUserDelta の権限チェックで
// 拒否されるか、更新APIの入力に項目が無い。
export const toDeltaInput = (
  detail: UserDetailDto,
  formModel: MeFormModel
): MeUpdateDeltaInput => {
  const before = fromDetail(detail)
  return {
    iconUrl: createDelta(before.iconUrl, formModel.iconUrl),
    name: createDelta(before.name, formModel.name),
    nameKana: createDelta(before.nameKana, formModel.nameKana),
    nameAlphabet: createDelta(before.nameAlphabet, formModel.nameAlphabet),
    phoneNumber: createDelta(before.phoneNumber, formModel.phoneNumber),
    birthday: createDelta(
      toApiDate(before.birthday),
      toApiDate(formModel.birthday)
    ),
    timezoneId: createDelta(before.timezoneId, formModel.timezoneId),
    emailNotification: createDelta(
      before.emailNotification,
      formModel.emailNotification
    ),
    slackUser: createSlackUserDelta(
      detail.slackUser ?? [],
      before.slackUser,
      formModel.slackUser,
      detail.uuid
    ),
    githubUserName: createDelta(
      before.githubUserName,
      formModel.githubUserName
    ),
  }
}

// Date はインスタンスが別なら createDelta の同値比較が効かないため、
// API に送る文字列へ揃えてから比較する。
const toApiDate = (v: DateV2 | undefined): string | undefined =>
  v ? dateV2Service.fromVoToApiRequest(v) : undefined

// APIはワークスペースの並び順を保証しないため、セルが表示・編集する1件が
// 読み込みのたびに入れ替わらないようワークスペースIDで並べてから先頭を取る。
const pickDisplayedSlackUser = (
  slackUsers: SlackUser[] | undefined
): SlackUser | undefined =>
  [...(slackUsers ?? [])].sort((a, b) =>
    (a.slackWorkspaceId ?? '').localeCompare(b.slackWorkspaceId ?? '')
  )[0]

// セルの操作は選んだワークスペースの連携だけを置き換えるものとして扱う。
// バックエンドは oldValue にあって newValue に無い連携を削除する
// （UpdateSlackMentionUsers）ため、他ワークスペースの連携は差分に載せないことで
// そのまま残す。切り替え先に既存の連携がある場合はその uuid を引き継ぐ。uuid が
// 無いとバックエンドは追加として扱い、ワークスペースとユーザーの組の一意制約に
// 違反して保存自体が失敗するため。uuid はセルの値ではなく取得結果から取る。セルは
// 保存中に編集が続くと保存前の値を基準に持ち続け、削除済みの連携の uuid を返すこと
// があり、バックエンドは oldValue に無い uuid を追加とも更新とも扱わず無視するため。
// userUuid と slackCollaborationWorkspaceUuid は永続化に必須だがセルは持たないため
// ここで補う。
// 変更判定にはワークスペースとユーザーに加えて表示名も含める。Slack側で表示名だけ
// 変わったときに、選び直せば追従させるため。
const createSlackUserDelta = (
  current: SlackUser[],
  before: SlackUser | undefined,
  after: SlackUser | undefined,
  userUuid: string
): IItemDelta<SlackUser[]> | undefined => {
  if (
    before?.slackWorkspaceId === after?.slackWorkspaceId &&
    before?.slackUserId === after?.slackUserId &&
    before?.slackUserName === after?.slackUserName
  ) {
    return undefined
  }
  const targetWorkspaceId = after?.slackWorkspaceId ?? before?.slackWorkspaceId
  const target = targetWorkspaceId
    ? current.find(v => v.slackWorkspaceId === targetWorkspaceId)
    : undefined
  return {
    oldValue: target ? [target] : [],
    newValue: after
      ? [
          {
            ...after,
            uuid: target?.uuid,
            userUuid: after.userUuid ?? userUuid,
            slackCollaborationWorkspaceUuid:
              target?.slackCollaborationWorkspaceUuid ??
              after.slackCollaborationWorkspaceUuid,
          },
        ]
      : [],
  }
}
